import { useEffect, useState } from 'react'

/**
 * Past chat requests typed by the member during this browser tab's lifetime — a "recall what I
 * typed" list for the chat composer's history dropdown (with full-text filter) and its
 * ArrowUp/ArrowDown shell-style recall. Deliberately NOT the same thing as the CRDT modification
 * HistoryPanel/HistoryModal (which tracks edits to document parts) and NOT persisted anywhere:
 * in-memory only, module-level so it survives ChatPanel unmounting (view switches) and outlives
 * `clearChat()`/bid switches — lost on reload or tab close, never written to
 * localStorage/sessionStorage or sent to a server.
 *
 * Same module-level-store + subscribe shape as agent-busy.ts's useAgentBusy — not the
 * localStorage-backed pattern in recent-bids.ts, which this must NOT resemble.
 */
export interface PromptEntry {
  id: string
  q: string
  /** When the request was submitted. */
  ts: number
  /** The passerelle job this request is running as, if any (immediate client-side errors, e.g.
   *  "no bid selected", have none — those are recorded already-ended). */
  jobId?: string
  /** When the exchange actually finished — sourced from the job's `finishedAt` once it resolves
   *  (see finishPrompt); undefined while still running. This is the "heure de fin" shown in the
   *  dropdown, not the submit time. */
  endedAt?: number
}

/** Kept small — this is a recall aid, not an archive. */
const MAX = 15

let entries: PromptEntry[] = [] // most-recent-first
const listeners = new Set<(e: PromptEntry[]) => void>()

function emit(): void {
  for (const l of listeners) l(entries)
}

/** Record a submitted request. Pass the passerelle job id when one was created (running turns);
 *  omit it for a request that already ended immediately client-side (e.g. no bid selected), which
 *  is recorded as ended right away. */
export function recordPrompt(q: string, jobId?: string): void {
  const text = q.trim()
  if (!text) return
  const now = Date.now()
  entries = [{ id: crypto.randomUUID(), q: text, ts: now, jobId, endedAt: jobId ? undefined : now }, ...entries].slice(0, MAX)
  emit()
}

/** Mark the request behind `jobId` as finished (its answer arrived, errored, or was cancelled).
 *  `endedAt` should be the job's own `finishedAt` when available (server/passerelle truth) rather
 *  than the client's own clock, so a slow poll tick doesn't skew the displayed end time. */
export function finishPrompt(jobId: string, endedAt: number): void {
  const idx = entries.findIndex((e) => e.jobId === jobId)
  if (idx === -1) return
  entries = entries.map((e, i) => (i === idx ? { ...e, endedAt } : e))
  emit()
}

/** Imperative snapshot (for reads outside React render). */
export function getPromptHistory(): PromptEntry[] {
  return entries
}

/** Reactive view of the past-requests list, most-recent-first. */
export function usePromptHistory(): PromptEntry[] {
  const [state, setState] = useState<PromptEntry[]>(entries)
  useEffect(() => {
    const l = (e: PromptEntry[]): void => setState(e)
    listeners.add(l)
    l(entries)
    return () => {
      listeners.delete(l)
    }
  }, [])
  return state
}